import PropTypes from 'prop-types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Divider, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  hasEligibleStoredMatches,
  NO_MATCHES_MESSAGE,
  similarPortfolioMatchRows,
} from '../../utils/nhStyleFingerprintSnapshot';

const SIMILAR_PORTFOLIOS_TITLE = 'Breweries with similar style portfolios (not flavor or quality)';

const NO_SIMILAR_BREWERIES_MESSAGE = 'No similar breweries at or under 0.3 variation.';

const shareGapTooltipSlot = {
  tooltip: { sx: { maxWidth: 480 } },
};

function ShareGapInfo({ name, shareGaps }) {
  return (
    <Tooltip title={shareGaps} slotProps={shareGapTooltipSlot}>
      <IconButton
        size="small"
        aria-label={`Share gaps for ${name}`}
        sx={{ color: 'text.secondary', p: 0.25 }}
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

ShareGapInfo.propTypes = {
  name: PropTypes.string.isRequired,
  shareGaps: PropTypes.string.isRequired,
};

function ShareGapLine({ name, score, shareGaps }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2">{`Variation score ${score}`}</Typography>
      <ShareGapInfo name={name} shareGaps={shareGaps} />
    </Box>
  );
}

ShareGapLine.propTypes = {
  name: PropTypes.string.isRequired,
  score: PropTypes.string.isRequired,
  shareGaps: PropTypes.string.isRequired,
};

function SimilarPortfolioRow({ row }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2">
        <Link component={RouterLink} to={row.path} sx={{ fontWeight: 700 }}>
          {row.name}
        </Link>
      </Typography>
      <ShareGapLine name={row.name} score={row.score} shareGaps={row.shareGaps} />
    </Box>
  );
}

SimilarPortfolioRow.propTypes = {
  row: PropTypes.shape({
    identityId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    score: PropTypes.string.isRequired,
    shareGaps: PropTypes.string.isRequired,
  }).isRequired,
};

function SimilarPortfolioRows({ rows }) {
  return rows.map((row) => (
    <SimilarPortfolioRow key={row.identityId} row={row} />
  ));
}

SimilarPortfolioRows.propTypes = {
  rows: PropTypes.arrayOf(SimilarPortfolioRow.propTypes.row).isRequired,
};

function NhStyleFingerprintMatches({ identity, identities = [] }) {
  if (!hasEligibleStoredMatches(identity)) {
    return (
      <Typography variant="body2" sx={{ my: 2 }}>
        {NO_MATCHES_MESSAGE}
      </Typography>
    );
  }

  const rows = similarPortfolioMatchRows(identity, identities);
  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ my: 2 }}>
        {NO_SIMILAR_BREWERIES_MESSAGE}
      </Typography>
    );
  }

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" component="h2" gutterBottom>
        {SIMILAR_PORTFOLIOS_TITLE}
      </Typography>
      <SimilarPortfolioRows rows={rows} />
      <Divider sx={{ my: 2 }} />
    </Box>
  );
}

NhStyleFingerprintMatches.propTypes = {
  identity: PropTypes.shape({
    matchEligible: PropTypes.bool,
    matches: PropTypes.array,
  }).isRequired,
  identities: PropTypes.arrayOf(PropTypes.object),
};

export default NhStyleFingerprintMatches;
